/**
 * Pure parsers that turn raw AppSync envelopes into "Thinking" lines for the
 * agentic-room transparency stream.
 *
 * Why this layer exists:
 *   The room's "Thinking" terminal needs *human-readable* one-liners — not raw
 *   JSON. The backend already emits structured log records (see
 *   ``stepflow_core.logger.S3BufferHandler.emit`` — every row has
 *   ``event_type`` + ``component`` + ``data``), but the strings are
 *   engineer-facing ("AGENT_TURN_COMPLETE"). This module is the canonical
 *   place that maps those internal labels to a short, friendly verb the user
 *   can read at glance ("Agent finished a turn").
 *
 * Wire vocabulary (from ``stepflow_core.events.EVENT_TYPES``):
 *   - ``LOG_BATCH``       payload.logs[] → many lines per envelope (the rich path).
 *   - ``LOG_LINE``        single row in payload (or top-level shape).
 *   - ``STATUS_UPDATE``   coarse run status — surfaced as a status pill.
 *   - ``AGENT_TOOL_CALL`` *reserved* (publisher not yet emitted; we still parse
 *                         it so the day it ships the UI lights up).
 *   - ``SFN_TRANSITION``  execution-level lifecycle — surfaced as a system
 *                         line, not per-state (the bridge does not currently
 *                         emit per-state events; see ``brain/sfn_appsync_bridge.py``).
 *
 * Design constraints:
 *   - **Pure.** No React, no clock, no DOM. The reducer owns sequencing
 *     and the UI owns presentation.
 *   - **Tolerant.** A row whose ``event_type`` we don't recognise still
 *     yields a usable line (the formatted ``message`` string), because we'd
 *     rather show "Hydrating router" than swallow it silently.
 *   - **Bounded data.** ``detail`` is a one-line, max ~120 char string. We
 *     never propagate the full ``data`` blob to the UI — that's what the
 *     Run-tab JSON viewer is for.
 */

import { AGENT_EVENTS, envelopeEventType } from '../constants/agentEvents.js';

/**
 * Backend ``event_type`` strings we recognise inside ``LOG_BATCH.payload.logs[]``.
 * Anything not listed here falls through to ``message``-based display.
 *
 * Source of truth: grep ``extra={"event_type": "..."}`` across
 * ``backend/api/sls/lambda-python3.9/{worker,brain,layers}``.
 */
const LOG_LINE_LABELS = Object.freeze({
  // Memory ----------------------------------------------------------------
  MEMORY_FETCHED: { label: 'Recalling memory', kind: 'memory' },
  MEMORY_RECALLED: { label: 'Recalling memory', kind: 'memory' },
  MEMORY_SUMMARIZED: { label: 'Summarizing memory', kind: 'memory' },
  CONTEXT_BUILT: { label: 'Building context window', kind: 'memory' },
  // Routing / agent lifecycle --------------------------------------------
  AGENT_HYDRATED: { label: 'Hydrating agent', kind: 'agent' },
  AGENT_RESOLVED: { label: 'Routing to agent', kind: 'agent' },
  AGENT_TURN_COMPLETE: { label: 'Agent finished a turn', kind: 'agent' },
  // LLM ------------------------------------------------------------------
  LLM_INFERENCE_COMPLETE: { label: 'Reasoning…', kind: 'llm' },
  LIGHT_MODE_TOOL_FINALIZER: { label: 'Drafting tool result', kind: 'llm' },
  // Tools ----------------------------------------------------------------
  TOOL_EXECUTION_COMPLETE: { label: 'Used tool', kind: 'tool' },
  // Documents / extraction ----------------------------------------------
  ASYNC_EXTRACTION_STARTED: { label: 'Reading document', kind: 'doc' },
  ASYNC_EXTRACTION_SUCCESS: { label: 'Document extracted', kind: 'doc' },
  // Vector / cold memory -------------------------------------------------
  VECTOR_INGESTION_COMPLETE: { label: 'Indexed cold memory', kind: 'memory' },
  // Consultation ---------------------------------------------------------
  CONSULTATION_COMPLETE: { label: 'Consulted internal expert', kind: 'agent' },
});

/**
 * Backend ``event_type`` rows we deliberately *suppress* from the thinking
 * stream. They're high-volume, low-signal noise that would push the
 * actually-interesting lines off the 5-line tail (and we already keep them
 * in the Run tab for ops triage).
 */
const SUPPRESSED_LOG_TYPES = Object.freeze(new Set([
  // Per-call $-cost telemetry — useful for ops, not for end-users; backend
  // emits one per LLM/embedding call which would dominate the tail.
  'LLM_USAGE_METRICS',
]));

/** Hard cap on a single line's ``detail`` field. */
const DETAIL_MAX_CHARS = 120;

/** @typedef {'memory'|'agent'|'llm'|'tool'|'doc'|'status'|'system'|'reasoning'|'unknown'} ThinkingKind */

/**
 * @typedef {Object} ThinkingLine
 * @property {string} id            Stable id; envelope.eventId+row index.
 * @property {string|number} [ts]   ISO or epoch ms.
 * @property {string} [agentId]     Best-effort agent (logicalId) attribution.
 * @property {string} [taskId]      Execution / task id.
 * @property {string} [executionId]
 * @property {string} [correlationId]
 * @property {string} label         One-line human verb.
 * @property {string} [detail]      Short, optional supplemental text.
 * @property {ThinkingKind} kind
 * @property {string} [eventType]   Original wire event type.
 * @property {string} [logEventType] Inner ``event_type`` for LOG_BATCH rows.
 */

function trimToOneLine(value, max = DETAIL_MAX_CHARS) {
  if (value == null) return undefined;
  let s;
  if (typeof value === 'string') s = value;
  else {
    try { s = JSON.stringify(value); } catch { s = String(value); }
  }
  s = s.replace(/\s+/g, ' ').trim();
  if (!s) return undefined;
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function asAgentId(value) {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t || undefined;
}

/**
 * Build a thinking line from a single ``LOG_BATCH`` row.
 *
 * Returns ``null`` for rows we suppress. Exported for the pure unit tests.
 */
export function thinkingLineFromLogRow(row, idx, parent) {
  if (!row || typeof row !== 'object') return null;
  const innerType = typeof row.event_type === 'string' ? row.event_type : '';
  if (innerType && SUPPRESSED_LOG_TYPES.has(innerType)) return null;

  const data = row.data && typeof row.data === 'object' ? row.data : {};
  const mapped = innerType ? LOG_LINE_LABELS[innerType] : undefined;
  const agentId = asAgentId(row.agentId) || asAgentId(data.agent_id) || asAgentId(data.agentId);
  const ts = row.timestamp || parent?.timestamp;

  let label;
  let detail;
  let kind;

  if (mapped) {
    label = mapped.label;
    kind = mapped.kind;
    if (innerType === 'TOOL_EXECUTION_COMPLETE') {
      const toolName = trimToOneLine(data.tool_name || data.toolName);
      const latency = data.latency_ms ?? data.latencyMs;
      const latencyStr = typeof latency === 'number' ? `${Math.round(latency)}ms` : '';
      detail = [toolName, latencyStr].filter(Boolean).join(' · ');
    } else if (innerType === 'LLM_INFERENCE_COMPLETE') {
      const phase = trimToOneLine(data.phase || data.mode);
      detail = phase ? `phase: ${phase}` : trimToOneLine(row.message);
    } else if (innerType === 'AGENT_RESOLVED' || innerType === 'AGENT_HYDRATED') {
      detail = agentId ? `→ ${agentId}` : trimToOneLine(row.message);
    } else {
      detail = trimToOneLine(row.message);
    }
  } else {
    label = trimToOneLine(row.message) || innerType || 'Working…';
    detail = innerType && row.message ? trimToOneLine(innerType) : undefined;
    kind = 'unknown';
  }

  return {
    id: `${parent?.eventId || 'log'}-${idx}`,
    ts,
    agentId,
    taskId: typeof row.taskId === 'string' ? row.taskId : undefined,
    label: label || 'Working…',
    detail,
    kind,
    eventType: AGENT_EVENTS.LOG_BATCH,
    logEventType: innerType || undefined,
  };
}

/**
 * Translate a full envelope into 0..N thinking lines.
 *
 *  - ``LOG_BATCH`` → one per row in ``payload.logs`` (suppressed types
 *    are skipped silently).
 *  - ``LOG_LINE`` → single line. We accept either ``payload`` shaped like a
 *    log row or top-level ``payload.message``.
 *  - ``STATUS_UPDATE`` → status pill (single line).
 *  - ``AGENT_TOOL_CALL`` → tool line (single line).
 *  - ``SFN_TRANSITION`` → system line on terminal/non-running statuses, so
 *    the user sees "Workflow paused" / "Workflow recovered" without raw
 *    JSON.
 *
 * @param {Record<string, unknown>} envelope
 * @returns {ThinkingLine[]}
 */
export function envelopeToThinkingLines(envelope) {
  if (!envelope || typeof envelope !== 'object') return [];
  const et = envelopeEventType(envelope);
  if (!et) return [];
  const payload = envelope.payload && typeof envelope.payload === 'object' ? envelope.payload : {};
  const baseId = typeof envelope.eventId === 'string' ? envelope.eventId : et;
  const ts = envelope.timestamp || envelope.created_at || envelope.serverTimestamp;
  const corr = typeof envelope.correlationId === 'string' ? envelope.correlationId : undefined;
  const exec = typeof envelope.executionId === 'string' ? envelope.executionId : undefined;

  switch (et) {
    case AGENT_EVENTS.LOG_BATCH: {
      const rows = Array.isArray(payload.logs) ? payload.logs : [];
      const out = [];
      for (let i = 0; i < rows.length; i += 1) {
        const line = thinkingLineFromLogRow(rows[i], i, envelope);
        if (line) {
          line.correlationId = corr;
          line.executionId = exec;
          out.push(line);
        }
      }
      return out;
    }
    case AGENT_EVENTS.LOG_LINE: {
      const ubStreamId = typeof payload.stream === 'string' ? payload.stream.trim() : '';
      if (ubStreamId === 'universal_builder') {
        return [];
      }
      const single = thinkingLineFromLogRow(payload, 0, envelope);
      if (single) {
        single.correlationId = corr;
        single.executionId = exec;
        single.eventType = AGENT_EVENTS.LOG_LINE;
        return [single];
      }
      return [];
    }
    case AGENT_EVENTS.STATUS_UPDATE: {
      const status = trimToOneLine(payload.status || payload.statusMessage || payload.message);
      if (!status) return [];
      return [{
        id: `${baseId}-status`,
        ts,
        label: 'Status',
        detail: status,
        kind: 'status',
        eventType: AGENT_EVENTS.STATUS_UPDATE,
        correlationId: corr,
        executionId: exec,
      }];
    }
    case AGENT_EVENTS.AGENT_REASONING: {
      // Generative-engine reasoning (Lead routing / reducer decision). No agent badge —
      // the user sees the reasoning text only, as a "Reasoning" thinking line. The terminal
      // WRAPS reasoning lines (ThinkingTerminal), so allow a generous cap (a full judge critique
      // fits) — 240 was clipping longer rationales; still bounded so a blob can't bloat the tail.
      const text = trimToOneLine(payload.text || payload.message, 800);
      if (!text) return [];
      return [{
        id: `${baseId}-reasoning`,
        ts,
        label: 'Reasoning',
        detail: text,
        kind: 'reasoning',
        eventType: AGENT_EVENTS.AGENT_REASONING,
        correlationId: corr,
        executionId: exec,
      }];
    }
    case AGENT_EVENTS.AGENT_TOOL_CALL: {
      const toolName = trimToOneLine(payload.toolName || payload.tool_name);
      const agentId = asAgentId(payload.agentName) || asAgentId(payload.agent_id) || asAgentId(payload.agentId);
      return [{
        id: `${baseId}-tool`,
        ts,
        agentId,
        label: toolName ? `Calling tool ${toolName}` : 'Calling tool',
        detail: trimToOneLine(payload.detail || payload.input),
        kind: 'tool',
        eventType: AGENT_EVENTS.AGENT_TOOL_CALL,
        correlationId: corr,
        executionId: exec,
      }];
    }
    case AGENT_EVENTS.SFN_TRANSITION: {
      const status = String(payload.status || '').toUpperCase();
      // RUNNING start emits its own ``EXECUTION_STARTED``/``UI_STATE``;
      // we surface only the non-RUNNING transitions to avoid duplicate
      // "Workflow started" rows in the tail.
      if (!status || status === 'RUNNING') return [];
      const label =
        status === 'SUCCEEDED' ? 'Workflow finished'
          : status === 'FAILED' ? 'Workflow failed'
            : status === 'TIMED_OUT' ? 'Workflow timed out'
              : status === 'ABORTED' ? 'Workflow aborted'
                : `Workflow ${status.toLowerCase()}`;
      return [{
        id: `${baseId}-sfn`,
        ts,
        label,
        detail: trimToOneLine(payload.error || payload.cause),
        kind: 'system',
        eventType: AGENT_EVENTS.SFN_TRANSITION,
        correlationId: corr,
        executionId: exec,
      }];
    }
    default:
      return [];
  }
}

/** Convenience for the reducer. Iterates an array, returns a flat list. */
export function envelopesToThinkingLines(envelopes) {
  if (!Array.isArray(envelopes)) return [];
  const out = [];
  for (let i = 0; i < envelopes.length; i += 1) {
    const lines = envelopeToThinkingLines(envelopes[i]);
    if (lines.length) out.push(...lines);
  }
  return out;
}
